import { type KeyboardEvent, type MouseEvent, useRef, useState } from "react";
import { css, styled } from "styled-components";

import { CheckIcon, ChevronIcon } from "common/components/icons";
import { UIButton } from "common/components/UIButton";
import { createScrollbarStyle } from "common/styles";
import { useOutsideClickDetection } from "common/utilities";
import { useTranslate } from "common/utilities/useTranslate";

const StyledContainer = styled.div`
  position: relative;
`;

const StyledResultsContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.searchDropdownBackground};
  border-radius: 5px;
  box-shadow: 0 0.5rem 1rem 0 rgba(0, 0, 0, 0.2);
  max-height: 28.25rem;
  overflow-y: auto;
  position: absolute;
  top: 54px;
  width: 100%;
  z-index: 1;

  ${createScrollbarStyle()}

  * {
    border-radius: 5px;
  }
`;

const StyledDropdownCardsContainer = styled.div`
  display: flex;
  flex-direction: column;

  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledNoResultsText = styled.div`
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledDropdownTrigger = styled.button<{ disabled?: boolean }>`
  background-color: ${({ theme }) => theme.colors.inputBackground}AA;
  border: 3px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 27px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.inputText};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  height: 3.375rem;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 0.8)};
  padding: 0 1.5rem 0 1.5rem;
  width: 100%;
  transition: all 0.3s;
  text-align: left;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  ${({ disabled }) =>
    !disabled &&
    css`
      opacity: 0.8;
      &:focus-visible,
      &:hover,
      &:focus {
        opacity: 1;
        background-color: ${({ theme }) => theme.colors.inputBackground};
        outline: none;
      }
    `}

  &::placeholder {
    color: ${({ theme }) => theme.colors.inputText};
    opacity: 0.8;
  }

  &.error {
    border: 3px solid ${({ theme }) => theme.colors.error};
  }
`;

const StyledDropdownIcon = styled(ChevronIcon)`
  transform: rotate(90deg);
  color: ${({ theme }) => theme.colors.inputText};
`;

const StyledButton = styled(UIButton)`
  background-color: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  aspect-ratio: 1;
  color: ${({ theme }) => theme.colors.inputText};

  &.error {
    border: 3px solid ${({ theme }) => theme.colors.error};
  }
`;

type SelectOption<T> = {
  label: string;
  value: T;
};

type SelectProps<T> = {
  accessibilityId?: string;
  value?: SelectOption<T>;
  placeholder?: string;
  options: (SelectOption<T> & { onClick: () => void })[];
  hasError?: boolean;
  isDisabled?: boolean;
};

export const Select = <T,>({
  accessibilityId,
  value,
  placeholder,
  options,
  hasError = false,
  isDisabled = false,
}: SelectProps<T>) => {
  const [areOptionsVisible, setAreOptionsVisible] = useState<boolean>(false);

  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resultsContainerRef = useRef<HTMLDivElement | null>(null);

  const toggleOptions = () => setAreOptionsVisible((prev) => !prev);

  useOutsideClickDetection(resultsContainerRef, () => setAreOptionsVisible(false));

  const renderOptions = () => {
    if (options.length === 0)
      return <StyledNoResultsText>{translate("app.search.results.no_results")}</StyledNoResultsText>;

    return (
      <StyledDropdownCardsContainer id={accessibilityId}>
        {options.map((option) => (
          <StyledButton
            key={option.label}
            onClick={(event: MouseEvent) => {
              event.preventDefault();
              option.onClick();
              setAreOptionsVisible(false);
            }}
          >
            {option.label}
            {value?.value === option.value && <CheckIcon width={14} height={14} />}
          </StyledButton>
        ))}
      </StyledDropdownCardsContainer>
    );
  };

  const closeResults = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setAreOptionsVisible(false);
    }
  };

  const handleTriggerClick = (event: MouseEvent) => {
    event.preventDefault();
    toggleOptions();
  };

  return (
    <StyledContainer ref={containerRef}>
      <StyledDropdownTrigger disabled={isDisabled} onClick={handleTriggerClick} className={hasError ? "error" : ""}>
        {value?.label ?? placeholder}
        <StyledDropdownIcon width={10} height={10} />
      </StyledDropdownTrigger>
      {areOptionsVisible && (
        <StyledResultsContainer ref={resultsContainerRef} onKeyDown={closeResults}>
          {renderOptions()}
        </StyledResultsContainer>
      )}
    </StyledContainer>
  );
};
